import { useEffect, useRef, useState } from 'react'
import confetti from 'canvas-confetti'
import { Sparkles, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTieredCountUp } from '@/hooks/useTieredCountUp'
import { LUCK_LABEL, luckGain, statusGainEntries } from '@/lib/status-gain'
import {
  POINT_TIER_LEVEL, POINT_TIER_STYLES, countUpDurationMs, cutinDurationMs, cutinTier,
  highlightDurationMs, highlightItems,
} from '@/lib/ai-result-tier'
import { playConfetti, playRollEnd, playTierUp, startRoll, stopAll, stopRoll, vibrate } from '@/lib/sfx'
import AiCutin from '@/components/feature/AiCutin'
import AiHighlight from '@/components/feature/AiHighlight'
import type { AnalyzeResult } from '@/api/achievements'

/** 演出のフェーズ。数値が大きいほど後の段階。 */
const BUILD = 0, CUTIN = 1, HIGHLIGHT = 2, REVEAL = 3, STATS = 4, POINTS = 5, LEVEL = 6
const LAST = LEVEL

/** 1行ずつせり上がる間隔 */
const ROW_STAGGER_MS = 80

/**
 * AI判定完了時の RPG 風リザルト。
 * 溜め →（高評価時のみ）カットイン → 高評価実績のハイライト → リビール → ステータス → ポイント → レベル の順に見せる。
 * 画面タップでいつでも最終状態までスキップできる。
 */
export default function AiResultOverlay({
  result,
  onClose,
}: {
  result: AnalyzeResult
  onClose: () => void
}) {
  const [step, setStep] = useState(BUILD)
  const [skipped, setSkipped] = useState(false)
  const firedRef = useRef(false)

  const skip = () => {
    stopAll()
    setSkipped(true)
    setStep(LAST)
  }

  const stats = statusGainEntries(result.total_status_gain)
  const luck = luckGain(result.total_status_gain)
  const leveledUp = result.level_after > result.level_before
  const cutin = cutinTier(result.items)
  const highlight = highlightItems(result.items)

  // フェーズを順に進める。stats はステータス行数に応じて待ち時間を伸ばす。
  useEffect(() => {
    if (step >= LAST) return
    const delays: Record<number, number> = {
      [BUILD]:  650,
      [CUTIN]:  cutin ? cutinDurationMs(cutin) : 0,
      [HIGHLIGHT]: highlightDurationMs(highlight.shown.length, highlight.rest),
      [REVEAL]: 500,
      [STATS]:  300 + stats.length * ROW_STAGGER_MS,
      [POINTS]: countUpDurationMs(result.total_earned_points) + 300,
    }
    const timer = setTimeout(() => setStep((s) => s + 1), delays[step])
    return () => clearTimeout(timer)
  }, [step, stats.length, cutin, result.total_earned_points, highlight.shown.length, highlight.rest])

  // レベルアップしたときだけ、レベル表示に到達した瞬間にパーティクルを出す。
  useEffect(() => {
    if (step < LEVEL || !leveledUp || firedRef.current) return
    firedRef.current = true
    confetti({
      particleCount: 220,
      spread: 120,
      startVelocity: 55,
      origin: { y: 0.55 },
      colors: ['#f5b942', '#b06bf0', '#34d3e0', '#ffffff'],
    })
    playConfetti()
  }, [step, leveledUp])

  // 段階の境目を越えるたびに光の輪を弾けさせる（key を変えてアニメーションを再生し直す）
  const [burstKey, setBurstKey] = useState(0)
  const { value: countedPoints, tier: currentPointTier } = useTieredCountUp(result.total_earned_points, {
    enabled: step >= POINTS,
    instant: skipped,
    onTierUp: (tier) => {
      setBurstKey((k) => k + 1)
      playTierUp(POINT_TIER_LEVEL[tier])
      vibrate(30)
    },
    // ドラムロールを締める（ジャン！）
    onComplete: () => {
      stopRoll()
      playRollEnd()
    },
  })

  // ポイントを数え始めたらドラムロール。スキップは skip() の stopAll で止まる
  useEffect(() => {
    if (step !== POINTS || skipped) return
    startRoll()
  }, [step, skipped])

  // 途中で閉じられてもロールが鳴り続けないようにする
  useEffect(() => () => stopRoll(), [])
  const pointStyle = POINT_TIER_STYLES[currentPointTier]

  const revealed = step >= REVEAL

  return (
    <div
      // 下はボトムナビ（前面に重なる）の高さ＋ホームバーぶん余白を取り、閉じるボタンまでスクロールで見えるようにする
      className="fixed inset-0 z-[70] flex flex-col items-center overflow-y-auto bg-black/85 px-6 pt-8 pb-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+1rem)] backdrop-blur-sm"
      onClick={skip}
    >
      {step === CUTIN && cutin && <AiCutin tier={cutin} />}
      {step === HIGHLIGHT && highlight.shown.length > 0 && (
        <AiHighlight items={highlight.shown} rest={highlight.rest} />
      )}

      {/* justify-center だと縦に溢れたとき上端（判定完了のマーク）へスクロールで戻れないため、my-auto で中央寄せする */}
      <div className="my-auto flex w-full flex-col items-center gap-4">
        {/* 溜め → 「判定完了」 */}
        <div className="relative flex h-24 w-24 shrink-0 items-center justify-center">
          {step === BUILD && (
            <div className="absolute h-16 w-16 animate-ping rounded-full bg-cyan-400/30" />
          )}
          <div
            className={`flex h-24 w-24 items-center justify-center rounded-full transition-all duration-700 ${
              revealed ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
            }`}
            style={{
              background: 'radial-gradient(circle, rgba(52,211,224,.35), transparent 70%)',
              boxShadow: '0 0 50px rgba(52,211,224,.5)',
            }}
          >
            <Sparkles size={48} className="text-cyan-300" style={{ filter: 'drop-shadow(0 0 12px rgba(52,211,224,.7))' }} />
          </div>
        </div>

        <div className={`shrink-0 text-center transition-opacity duration-700 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-2xl font-bold text-white">判定完了</p>
          <p className="mt-1 text-sm text-white/70">実績 {result.processed} 件</p>
        </div>

        {/* 獲得ステータス（1行ずつせり上がる） */}
        <div className="w-full max-w-xs shrink-0 space-y-1.5 md:max-w-sm">
          {stats.map(({ key, label, val }, i) => (
            <div
              key={key}
              className={`flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 transition-all duration-500 ${
                step >= STATS ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
              }`}
              style={{ transitionDelay: `${i * ROW_STAGGER_MS}ms` }}
            >
              <span className="text-sm text-white/80">{label}</span>
              <span className="text-base font-bold text-cyan-300">+{val}</span>
            </div>
          ))}
          {luck !== 0 && (
            <div
              className={`flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 transition-all duration-500 ${
                step >= STATS ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
              }`}
              style={{ transitionDelay: `${stats.length * ROW_STAGGER_MS}ms` }}
            >
              <span className="text-sm text-white/80">{LUCK_LABEL}</span>
              <span className={`text-base font-bold ${luck > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {luck > 0 ? `+${luck}` : luck}
              </span>
            </div>
          )}
        </div>

        {/* 合計ポイント */}
        <div className={`relative shrink-0 text-center transition-opacity duration-500 ${step >= POINTS ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-xs tracking-[.3em] text-white/50">EARNED POINTS</p>
          <p
            className={`font-bold transition-all duration-300 ${pointStyle.className}`}
            style={{ textShadow: pointStyle.textShadow }}
          >
            +{countedPoints.toLocaleString()}
          </p>
          {burstKey > 0 && !skipped && (
            <span
              key={burstKey}
              className={`tier-burst pointer-events-none absolute left-1/2 top-1/2 -ml-12 -mt-12 h-24 w-24 rounded-full border-4 ${pointStyle.burst}`}
              aria-hidden
            />
          )}
        </div>

        {/* レベル */}
        <div className={`shrink-0 text-center transition-all duration-500 ${step >= LEVEL ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
          {leveledUp && (
            <p className="mb-1 animate-pulse text-lg font-bold tracking-widest text-amber-300"
              style={{ textShadow: '0 0 20px rgba(245,185,66,.8)' }}>
              LEVEL UP!
            </p>
          )}
          <p className="text-white/80">
            {leveledUp ? (
              <>
                <span className="text-lg">Lv.{result.level_before}</span>
                <span className="mx-2 text-white/40">→</span>
                <span className="text-3xl font-bold text-amber-300">Lv.{result.level_after}</span>
              </>
            ) : (
              <span className="text-2xl font-bold">Lv.{result.level_after}</span>
            )}
          </p>
        </div>

        {/* 判定した実績の内訳 */}
        <div
          className={`w-full max-w-xs shrink-0 space-y-1 overflow-y-auto transition-opacity duration-500 md:max-w-sm ${
            step >= LEVEL ? 'max-h-[30vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          {result.items.map((item) => (
            <div key={item.id} className="flex items-center gap-2 rounded-md bg-white/5 px-2.5 py-1.5">
              <span className="line-clamp-1 flex-1 text-xs text-white/80">{item.title}</span>
              {item.ai_rating !== null && (
                <span className="flex shrink-0 items-center gap-0.5 text-[11px] text-amber-300">
                  <Star size={10} fill="currentColor" />
                  {item.ai_rating}
                </span>
              )}
              <span className="shrink-0 text-[11px] font-medium text-white/60">+{item.earned_points}</span>
            </div>
          ))}
        </div>

        <Button
          className={`min-h-[44px] shrink-0 transition-opacity duration-500 ${
            step >= LEVEL ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
        >
          閉じる
        </Button>
      </div>
    </div>
  )
}
